import Vue from "vue";
import axios from "axios";
import VueAxios from "vue-axios";
import store from '@/store'
import { FETCH_AUTH_ROLES_DATA } from "@/store/actions.type";
import { SET_TIMEOUT_ERROR, SET_IS_LOADING } from "@/store/mutations.type";
import { handle } from "@/utils/apiErrorHandler.js";

const CLINICIAN_URL = '/api/org/clinician'
const PATIENT_URL = '/api/org/clinician'
const ROLES_URL = 'api/admin/org/roles'
const USERS_URL = 'api/admin/org'
const PROCEDURE_URL = 'api/org/procedure'
const SURVEYS_URL = 'api/org/survey'
const EDUCATION_URL = 'api/org/education'
const ORGANIZATION_URL = 'api/admin/org'
const FITBIT_URL = 'api/fitbit'
const AGREEMENT_URL = 'api/org/agreement'
const ORGANIZATION_NAME_URL = 'api/org'
const OPEN_PATIENT_URL = 'api/patient'
const TIME_TRACKING = 'api/org/clinician/patient'
const DEVICE_DATA = '/api/org/clinician/patient/device'
const REPORT_URL = '/api/org/report'
const INSURANCE_REPORT_URL = '/api/org/insurance'
const PUSH_TO_EMR_URL = '/api/org/emrpush'
const AVAILABLE_ORGANISATIONS = '/api/org'
const AVAILABLE_DEVICES = '/api/admin/device'
const ACCOUNT_SETUP_AGREEMENT = '/api/patient/agreement'
const BILLING_APPROVAL = '/api/org/clinician/billing/approval'
// temp solution for org admin to approve billing codes as billing guy. Would go in req body in future rather then params
const BILLING_APPROVAL_WITH_PARAMS = '/api/org/clinician/billing/approval?approveAs=ROLE_BILLING'
const MR_NUMBER = 'api/org/clinician/patient/emr/search'
const FHIR_URL = '/api/v1/fhir'
const COMPLIANCE_URL = '/api/org/compliance'
const APPOINTMENT_URL = '/api/org/clinician/appointments'

const ApiService = {

    init() {
        Vue.use(VueAxios, axios);
        Vue.axios.defaults.baseURL = process.env.VUE_APP_API_URL;
    },

    async setHeader() {
        await store.dispatch(FETCH_AUTH_ROLES_DATA).then(response => {
            Vue.axios.defaults.headers.common["Authorization"] = (response != null ? `Bearer ${response.token}` : `Bearer `);
            Vue.axios.defaults.headers["Content-Type"] = "application/json";
            Vue.axios.defaults.headers["Access-Control-Allow-Origin"] = '*'
        })

    },

    async setFhirHeader(response) {
        Vue.axios.defaults.headers.common["Authorization"] = (response != null ? `Bearer ${response.token}` : `Bearer `);
        Vue.axios.defaults.headers["Content-Type"] = "application/json";
        Vue.axios.defaults.headers["Access-Control-Allow-Origin"] = '*';
        Vue.axios.defaults.headers.common["organization"] = response != null ? response.organization : 0;
    },

    setOrganisationId(organisationId) {
        Vue.axios.defaults.headers["organization"] = organisationId
    },

    removeOrganisationId() {
        delete Vue.axios.defaults.headers["organization"]
    },

    query(resource, params) {
        return Vue.axios.get(resource, { params }, { timeout: 15000 }).catch((error) => {
            if (error == "Error: Network Error") {
                store.commit(SET_TIMEOUT_ERROR, { title: 'Error', message: 'We seemed to have encountered an error.  Please retry.  If problem persists contact administrator.', isOpen: true })
                store.commit(SET_IS_LOADING, false)
                throw error
            }
            else if (error.code === 'ECONNABORTED') {
                store.commit(SET_TIMEOUT_ERROR, { title: 'Error', message: 'We seemed to have encountered an error.  Please retry.  If problem persists contact administrator.', isOpen: true })
                store.commit(SET_IS_LOADING, false)
                throw error
            }
            else {
                store.commit(SET_IS_LOADING, false);
                handle(error);
            }
        });
    },

    get(resource, slug = '') {
        return Vue.axios.get(`${resource}/${slug}`, { timeout: 15000 }).catch((error) => {
            if (error == "Error: Network Error") {
                store.commit(SET_TIMEOUT_ERROR, { title: 'Error', message: 'We seemed to have encountered an error.  Please retry.  If problem persists contact administrator.', isOpen: true })
                store.commit(SET_IS_LOADING, false)
                throw error
            }
            else if (error.code === 'ECONNABORTED') {
                store.commit(SET_TIMEOUT_ERROR, { title: 'Error', message: 'We seemed to have encountered an error.  Please retry.  If problem persists contact administrator.', isOpen: true })
                store.commit(SET_IS_LOADING, false)
                throw error
            }
            else {
                store.commit(SET_IS_LOADING, false);
                handle(error);
            }
        });
    },

    getReport(resource, slug = '') {
        return Vue.axios.get(`${resource}/${slug}`, { timeout: 15000, responseType: 'blob' }).catch((error) => {
            if (error == "Error: Network Error") {
                store.commit(SET_TIMEOUT_ERROR, { title: 'Error', message: 'We seemed to have encountered an error.  Please retry.  If problem persists contact administrator.', isOpen: true })
                store.commit(SET_IS_LOADING, false)
                throw error
            }
            else if (error.code === 'ECONNABORTED') {
                store.commit(SET_TIMEOUT_ERROR, { title: 'Error', message: 'We seemed to have encountered an error.  Please retry.  If problem persists contact administrator.', isOpen: true })
                store.commit(SET_IS_LOADING, false)
                throw error
            }
            else {
                store.commit(SET_IS_LOADING, false);
                handle(error);
            }
        });
    },

    getMonthlyReport(resource, startDate, endDate, patientId) {
        return Vue.axios.get(`${resource}/${patientId}?from=${startDate}&to=${endDate}`, { timeout: 30000, responseType: 'blob' }).catch((error) => {
            if (error == "Error: Network Error") {
                store.commit(SET_TIMEOUT_ERROR, { title: 'Error', message: 'We seemed to have encountered an error.  Please retry.  If problem persists contact administrator.', isOpen: true })
                store.commit(SET_IS_LOADING, false)
                throw error
            }
            else if (error.code === 'ECONNABORTED') {
                store.commit(SET_TIMEOUT_ERROR, { title: 'Error', message: 'We seemed to have encountered an error.  Please retry.  If problem persists contact administrator.', isOpen: true })
                store.commit(SET_IS_LOADING, false)
                throw error
            }
            else {
                store.commit(SET_IS_LOADING, false);
                handle(error);
            }
        });
    },

    post(resource, params) {

        return Vue.axios.post(`${resource}`, params, { timeout: 15000 }).catch((error) => {
            if (error == "Error: Network Error") {
                store.commit(SET_TIMEOUT_ERROR, { title: 'Error', message: 'We seemed to have encountered an error.  Please retry.  If problem persists contact administrator.', isOpen: true })
                store.commit(SET_IS_LOADING, false)
                throw error
            }
            else if (error.code === 'ECONNABORTED') {
                store.commit(SET_TIMEOUT_ERROR, { title: 'Error', message: 'We seemed to have encountered an error.  Please retry.  If problem persists contact administrator.', isOpen: true })
                store.commit(SET_IS_LOADING, false)
                throw error
            }
            else {
                store.commit(SET_IS_LOADING, false);
                handle(error);
            }
        });
    },

    update(resource, slug, params) {
        return Vue.axios.put(`${resource}/${slug}`, params, { timeout: 15000 }).catch((error) => {
            if (error == "Error: Network Error") {
                store.commit(SET_TIMEOUT_ERROR, { title: 'Error', message: 'We seemed to have encountered an error.  Please retry.  If problem persists contact administrator.', isOpen: true })
                store.commit(SET_IS_LOADING, false)
                throw error
            }
            else if (error.code === 'ECONNABORTED') {
                store.commit(SET_TIMEOUT_ERROR, { title: 'Error', message: 'We seemed to have encountered an error.  Please retry.  If problem persists contact administrator.', isOpen: true })
                store.commit(SET_IS_LOADING, false)
                throw error
            }
            else {
                store.commit(SET_IS_LOADING, false);
                handle(error);
            }
        });
    },
    put(resource, params) {
        return Vue.axios.put(resource, params, { timeout: 15000 }).catch((error) => {
            if (error == "Error: Network Error") {
                store.commit(SET_TIMEOUT_ERROR, { title: 'Error', message: 'We seemed to have encountered an error.  Please retry.  If problem persists contact administrator.', isOpen: true })
                store.commit(SET_IS_LOADING, false)
                throw error
            }
            else if (error.code === 'ECONNABORTED') {
                store.commit(SET_TIMEOUT_ERROR, { title: 'Error', message: 'We seemed to have encountered an error.  Please retry.  If problem persists contact administrator.', isOpen: true })
                store.commit(SET_IS_LOADING, false)
                throw error
            }
            else {
                store.commit(SET_IS_LOADING, false);
                handle(error);
            }
        });
    },

    delete(resource) {
        return Vue.axios.delete(resource, { timeout: 15000 }).catch((error) => {
            if (error == "Error: Network Error") {
                store.commit(SET_TIMEOUT_ERROR, { title: 'Error', message: 'We seemed to have encountered an error.  Please retry.  If problem persists contact administrator.', isOpen: true })
                store.commit(SET_IS_LOADING, false)
                throw error
            }
            else if (error.code === 'ECONNABORTED') {
                store.commit(SET_TIMEOUT_ERROR, { title: 'Error', message: 'We seemed to have encountered an error.  Please retry.  If problem persists contact administrator.', isOpen: true })
                store.commit(SET_IS_LOADING, false)
                throw error
            }
            else {
                store.commit(SET_IS_LOADING, false);
                handle(error);
            }

        });
    },

    patch(resource, params) {
        return Vue.axios.patch(resource, params, { timeout: 15000 }).catch((error) => {
            if (error == "Error: Network Error") {
                store.commit(SET_TIMEOUT_ERROR, { title: 'Error', message: 'We seemed to have encountered an error.  Please retry.  If problem persists contact administrator.', isOpen: true })
                store.commit(SET_IS_LOADING, false)
                throw error
            }
            else if (error.code === 'ECONNABORTED') {
                store.commit(SET_TIMEOUT_ERROR, { title: 'Error', message: 'We seemed to have encountered an error.  Please retry.  If problem persists contact administrator.', isOpen: true })
                store.commit(SET_IS_LOADING, false)
                throw error
            }
            else {
                store.commit(SET_IS_LOADING, false);
                handle(error);
            }
        });
    },

}

export default ApiService

export const ClinicianService = {

    get(slug) {
        return ApiService.get(CLINICIAN_URL, slug)
    },

    create(slug, params) {
        return ApiService.post(CLINICIAN_URL + '/' + slug, params)
    },

    update(slug, params) {
        return ApiService.update(CLINICIAN_URL, slug, params)
    },

    destroy(slug) {
        return ApiService.delete(CLINICIAN_URL`/${slug}`)
    }

}

export const MRNumberService = {
    post(slug) {
        return ApiService.post(MR_NUMBER, slug)
    }
}

export const AccountSetupAgreementService = {
    get(slug) {
        return ApiService.get(ACCOUNT_SETUP_AGREEMENT, slug)
    }
}

export const ReportService = {
    get(slug) {
        return ApiService.getReport(REPORT_URL, slug)
    },
    getMonthlyReport(startDate, endDate, patientId) {
        return ApiService.getMonthlyReport(REPORT_URL, startDate, endDate, patientId)
    },
    getBillingData(startDate, endDate, physicianId) {
        const slug = `?from=${startDate}&to=${endDate}&physicianId=${physicianId}`
        return ApiService.get(REPORT_URL, slug)
    },
    getBillableCodes(slug) {
        return ApiService.get(REPORT_URL, slug)
    },
    getInsuranceReport(startDate, endDate, patientId) {
        return ApiService.getMonthlyReport(INSURANCE_REPORT_URL, startDate, endDate, patientId)
    },
    pushToEMR(startDate, endDate, patientId) {
        return ApiService.post(`${PUSH_TO_EMR_URL}/${patientId}?from=${startDate}&to=${endDate}`, {})
    }
}

export const BillingApproval = {
    post(slug) {
        return ApiService.post(BILLING_APPROVAL, slug)
    },
    put(slug) {
        return ApiService.put(BILLING_APPROVAL + '?approveAs=' + localStorage.getItem("role"), slug)
    }
}

export const AvailableOrganisationsService = {
    get() {
        return ApiService.get(AVAILABLE_ORGANISATIONS)
    },
    post(slug) {
        return ApiService.post(AVAILABLE_ORGANISATIONS, slug)
    },
    put(slug) {
        return ApiService.put(AVAILABLE_ORGANISATIONS, slug)
    },
}

export const AvailableDevicesService = {
    get() {
        return ApiService.get(AVAILABLE_DEVICES)
    },
    post(slug) {
        return ApiService.post(AVAILABLE_DEVICES, slug)
    },
    put(slug) {
        return ApiService.put(AVAILABLE_DEVICES, slug)
    },
    destroy(slug) {
        return ApiService.delete(AVAILABLE_DEVICES + "/" + slug);
    },
}

export const RolesService = {

    get() {
        return ApiService.get(ROLES_URL)
    },

}

export const UsersService = {

    get(slug) {
        return ApiService.get(USERS_URL, slug)
    },

    create(slug, params) {
        return ApiService.post(USERS_URL + '/' + slug, params)
    },

    update(slug, params) {
        return ApiService.update(USERS_URL, slug, params)
    },

    destroy(slug) {
        return ApiService.delete(USERS_URL`/${slug}`)
    },

}

export const ProceduresService = {

    get(slug) {
        return ApiService.get(PROCEDURE_URL, slug)
    },

    create(slug, params) {
        return ApiService.post(PROCEDURE_URL`/${slug}`, params)
    },

    update(slug, params) {
        return ApiService.update(PROCEDURE_URL, slug, params)
    },

    destroy(slug) {
        return ApiService.delete(PROCEDURE_URL`/${slug}`)
    }

}

export const SurveysService = {

    get(slug) {
        return ApiService.get(SURVEYS_URL, slug)
    },

    create(slug, params) {
        return ApiService.post(SURVEYS_URL`/${slug}`, params)
    },

    update(slug, params) {
        return ApiService.update(SURVEYS_URL, slug, params)
    },

    destroy(slug) {
        return ApiService.delete(SURVEYS_URL`/${slug}`)
    }

}


export const EducationsService = {

    get(slug) {
        return ApiService.get(EDUCATION_URL, slug)
    },

    getAll(slug, params) {
        return ApiService.post(EDUCATION_URL + "/all", params)
    },

}

export const OrganizationService = {

    get(slug) {
        return ApiService.get(ORGANIZATION_URL, slug)
    },

    create(slug, params) {
        return ApiService.post(ORGANIZATION_URL + '/' + slug, params)
    },

    update(slug, params) {
        return ApiService.update(ORGANIZATION_URL, slug, params)
    },

    destroy(slug) {
        return ApiService.delete(ORGANIZATION_URL + '/' + slug)
    }

}

export const FitbitService = {

    get(slug) {
        return ApiService.get(FITBIT_URL, slug)
    },

    create(slug, params) {
        return ApiService.post(FITBIT_URL + slug, params)
    },

    update(slug, params) {
        return ApiService.update(FITBIT_URL, slug, params)
    },

    destroy(slug) {
        return ApiService.delete(FITBIT_URL`/${slug}`)
    },

    setupAuth(patientID, authcode) {

        return ApiService.post('/api/fitbit/patient/' + patientID + '/auth/' + authcode, "")
    },


    fitbitValidate(fitbitDto, authorizationCode) {
        const url = fitbitDto.accessTokenUrl + "&code=" + authorizationCode;
        return new Promise((resolve, reject) => {
            axios({
                method: 'POST',
                url: url,
                data: '',
                headers: {
                    Authorization: "Basic " + fitbitDto.authorization,
                    "Content-Type": "application/x-www-form-urlencoded",
                    "Origin": "https://staging.galileorpm.com",
                    'Access-Control-Allow-Methods': 'POST,GET,DELETE,OPTIONS,PATCH'
                }
            }).then(response => {
                resolve(response)
            }).catch(error => {
                (error) ? reject(error) : reject(new Error("No response from Fitbit"));
            })
        })
    }

}

export const PatientsService = {

    get(slug) {
        return ApiService.get(PATIENT_URL, slug)
    },

    create(slug, params) {
        return ApiService.post(PATIENT_URL + '/' + slug, params)
    },

    update(slug, params) {
        return ApiService.update(PATIENT_URL, slug, params)
    },

    destroy(slug) {
        return ApiService.delete(PATIENT_URL`/${slug}`)
    }

}

export const AgreementService = {
    get(slug) {
        return ApiService.get(AGREEMENT_URL, slug)
    },
}

export const OrganizationNameService = {
    get(slug) {
        return ApiService.get(ORGANIZATION_NAME_URL, slug)
    },
    create(slug, params) {
        return ApiService.post(ORGANIZATION_NAME_URL + '/' + slug, params)
    },
}


export const OpenPatientService = {
    get(slug) {
        return ApiService.get(OPEN_PATIENT_URL, slug)
    },

    create(slug, params) {
        return ApiService.post(OPEN_PATIENT_URL + '/' + slug, params)
    },

}


export const TimeTrackingService = {
    get(slug) {
        return ApiService.get(TIME_TRACKING, slug)
    },

    create(slug, params) {
        return ApiService.post(TIME_TRACKING + '/' + slug, params)
    },

    update(slug, params) {
        return ApiService.update(TIME_TRACKING, slug, params)
    },

    destroy(slug) {
        return ApiService.delete(TIME_TRACKING + '/' + slug)
    }



}

export const DeviceService = {
    get(slug) {
        return ApiService.get(DEVICE_DATA + '/' + slug)
    },

    create(slug, params) {
        return ApiService.post(DEVICE_DATA + '/' + slug, params)
    },

    destroy(slug) {
        return ApiService.delete(DEVICE_DATA + '/' + slug)
    }

}


axios.interceptors.request.use(async req => {
    await store.dispatch(FETCH_AUTH_ROLES_DATA).then(response => {
        if (response != null && !req.url.includes('fitbit.com')) {
            req.headers['Authorization'] = "Bearer " + response.token;
        }
    })
    return req;
});

export const FhirService = {

    get(slug, params) {
        return ApiService.query(FHIR_URL + '/' + slug, params)
    },

    create(slug, params) {
        return ApiService.post(FHIR_URL + '/' + slug, params)
    },

    update(slug, params) {
        return ApiService.update(FHIR_URL, slug, params)
    }

}

export const ComplianceService = {

    get(slug, params) {
        return ApiService.query(COMPLIANCE_URL + '/' + slug, params)
    }

}

export const AppointmentService = {

    getToken(payload) {
        return ApiService.post(APPOINTMENT_URL + '/token', payload)
    },

    getAllAppointments(physicianId) {
        return ApiService.query(APPOINTMENT_URL, { physicianId: physicianId });
    },

    getAllAppointmentById(id) {
        return ApiService.get(APPOINTMENT_URL + '/' + id);
    },

    getRecentAppointments(physicianId) {
        return ApiService.query(APPOINTMENT_URL + '/recent', { physicianId: physicianId });
    },

    createAppointment(payload) {
        return ApiService.post(APPOINTMENT_URL, payload);
    },

    updateAppointment(id, payload) {
        return ApiService.put(APPOINTMENT_URL + '/' + id, payload);
    },

    deleteAppointment(id) {
        return ApiService.delete(APPOINTMENT_URL + '/' + id);
    },

    acceptOrRejectAppointment(id, status) {
        return ApiService.patch(APPOINTMENT_URL + '/' + id + '/' + status);
    },

}
